import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type { Tag, TagWithCount, CreateTagInput } from "@shared/types";
import { WSEvent } from "@shared/constants";
import { api } from "@/utils/api";
import type { Socket } from "socket.io-client";

export const useTagsStore = defineStore("tags", () => {
  // State
  const tags = ref<Tag[]>([]);
  const tagsWithCount = ref<TagWithCount[]>([]);
  const resultTags = ref<Map<number, Tag[]>>(new Map());
  const isLoading = ref(false);

  // Getters
  const tagById = computed(
    () => (id: number) =>
      tags.value.find((t) => t.id === id),
  );

  const tagByName = computed(
    () => (name: string) =>
      tags.value.find((t) => t.name.toLowerCase() === name.toLowerCase()),
  );

  const popularTags = computed(() => {
    return [...tagsWithCount.value]
      .sort((a, b) => b.usageCount - a.usageCount)
      .slice(0, 10);
  });

  // Actions

  /**
   * Fetch all tags
   */
  const fetchTags = async () => {
    try {
      isLoading.value = true;
      const data = await api.get<Tag[]>("/api/tags");
      tags.value = data;
    } catch (error) {
      console.error("Failed to fetch tags:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch all tags with usage count
   */
  const fetchTagsWithCount = async () => {
    try {
      isLoading.value = true;
      const data = await api.get<TagWithCount[]>("/api/tags/with-count");
      tagsWithCount.value = data;
    } catch (error) {
      console.error("Failed to fetch tags with count:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch tags for a result
   */
  const fetchTagsForResult = async (resultId: number) => {
    try {
      const data = await api.get<Tag[]>(`/api/tags/result/${resultId}`);
      resultTags.value.set(resultId, data);
      return data;
    } catch (error) {
      console.error("Failed to fetch tags for result:", error);
      throw error;
    }
  };

  /**
   * Create a new tag
   */
  const createTag = async (input: CreateTagInput) => {
    try {
      const tag = await api.post<Tag>("/api/tags", input);
      tags.value.push(tag);
      return tag;
    } catch (error) {
      console.error("Failed to create tag:", error);
      throw error;
    }
  };

  /**
   * Update a tag
   */
  const updateTag = async (id: number, input: Partial<CreateTagInput>) => {
    try {
      const tag = await api.put<Tag>(`/api/tags/${id}`, input);
      const index = tags.value.findIndex((t) => t.id === id);
      if (index !== -1) {
        tags.value[index] = tag;
      }
      return tag;
    } catch (error) {
      console.error("Failed to update tag:", error);
      throw error;
    }
  };

  /**
   * Delete a tag
   */
  const deleteTag = async (id: number) => {
    try {
      await api.delete(`/api/tags/${id}`);
      tags.value = tags.value.filter((t) => t.id !== id);
      tagsWithCount.value = tagsWithCount.value.filter((t) => t.id !== id);

      // Remove from result tags
      resultTags.value.forEach((tagList, resultId) => {
        resultTags.value.set(
          resultId,
          tagList.filter((t) => t.id !== id),
        );
      });
    } catch (error) {
      console.error("Failed to delete tag:", error);
      throw error;
    }
  };

  /**
   * Add tag to result
   */
  const addTagToResult = async (resultId: number, tagId: number) => {
    try {
      await api.post("/api/tags/add-to-result", { resultId, tagId });

      // Update local state
      const tag = tagById.value(tagId);
      if (tag) {
        const current = resultTags.value.get(resultId) || [];
        if (!current.find((t) => t.id === tagId)) {
          resultTags.value.set(resultId, [...current, tag]);
        }
      }
    } catch (error) {
      console.error("Failed to add tag to result:", error);
      throw error;
    }
  };

  /**
   * Remove tag from result
   */
  const removeTagFromResult = async (resultId: number, tagId: number) => {
    try {
      await api.post("/api/tags/remove-from-result", { resultId, tagId });

      // Update local state
      const current = resultTags.value.get(resultId) || [];
      resultTags.value.set(
        resultId,
        current.filter((t) => t.id !== tagId),
      );
    } catch (error) {
      console.error("Failed to remove tag from result:", error);
      throw error;
    }
  };

  /**
   * Get tags for a result (from cache or fetch)
   */
  const getTagsForResult = async (resultId: number): Promise<Tag[]> => {
    if (resultTags.value.has(resultId)) {
      return resultTags.value.get(resultId)!;
    }
    return await fetchTagsForResult(resultId);
  };

  /**
   * Setup WebSocket listeners
   */
  const setupWebSocketListeners = (socket: Socket) => {
    socket.on(WSEvent.TAG_CREATED, ({ tag }: { tag: Tag }) => {
      if (!tags.value.find((t) => t.id === tag.id)) {
        tags.value.push(tag);
      }
    });

    socket.on(WSEvent.TAG_UPDATED, ({ tag }: { tag: Tag }) => {
      const index = tags.value.findIndex((t) => t.id === tag.id);
      if (index !== -1) {
        tags.value[index] = tag;
      }
    });

    socket.on(WSEvent.TAG_DELETED, ({ tagId }: { tagId: number }) => {
      tags.value = tags.value.filter((t) => t.id !== tagId);
      tagsWithCount.value = tagsWithCount.value.filter((t) => t.id !== tagId);

      // Remove from result tags
      resultTags.value.forEach((tagList, resultId) => {
        resultTags.value.set(
          resultId,
          tagList.filter((t) => t.id !== tagId),
        );
      });
    });

    socket.on(
      WSEvent.TAG_ADDED_TO_RESULT,
      ({
        resultId,
        tagId,
      }: {
        resultId: number;
        tagId: number;
        resultTag: unknown;
      }) => {
        const tag = tagById.value(tagId);
        if (tag) {
          const current = resultTags.value.get(resultId) || [];
          if (!current.find((t) => t.id === tagId)) {
            resultTags.value.set(resultId, [...current, tag]);
          }
        }
      },
    );

    socket.on(
      WSEvent.TAG_REMOVED_FROM_RESULT,
      ({ resultId, tagId }: { resultId: number; tagId: number }) => {
        const current = resultTags.value.get(resultId) || [];
        resultTags.value.set(
          resultId,
          current.filter((t) => t.id !== tagId),
        );
      },
    );
  };

  return {
    // State
    tags,
    tagsWithCount,
    resultTags,
    isLoading,

    // Getters
    tagById,
    tagByName,
    popularTags,

    // Actions
    fetchTags,
    fetchTagsWithCount,
    fetchTagsForResult,
    createTag,
    updateTag,
    deleteTag,
    addTagToResult,
    removeTagFromResult,
    getTagsForResult,
    setupWebSocketListeners,
  };
});
